import React from 'react';
import { Dropdown, Form } from 'react-bootstrap';
import { FaChevronDown, FaTimes } from 'react-icons/fa';
import PropTypes from 'prop-types';

const CommonSelect = ({
  label,
  value,
  onChange,
  options,
  placeholder,
  error,
  touched,
  loading,
  name,
  required,
  isValid,
  multiple,
  description,
  className = 'mb-3',
  showSearch,
  onSearch,
  selectAll,
  onLoadMore,
  isLoadingMore,
  hasMore,
  onBlur
}) => {
  const [searchTerm, setSearchTerm] = React.useState('');
  const [isOpen, setIsOpen] = React.useState(false);
  const isFirstMount = React.useRef(true);

  React.useEffect(() => {
    if (onSearch) {
      if (isFirstMount.current && searchTerm === '') {
        isFirstMount.current = false;
        return;
      }
      if (searchTerm === '') {
        onSearch('');
        return;
      }
      const delayDebounceFn = setTimeout(() => {
        onSearch(searchTerm);
      }, 500);
      return () => clearTimeout(delayDebounceFn);
    }
  }, [searchTerm, onSearch]);

  let userId = null;
  const userInfoStr = localStorage.getItem('userInfo');
  if (userInfoStr) {
    const userInfo = JSON.parse(userInfoStr);
    userId = String(userInfo.id);
  }

  let resolvedValues = [];
  if (multiple && Array.isArray(value)) {
    resolvedValues = value.map((v) => (String(v) === 'self' && userId ? userId : String(v)));
    resolvedValues = [...new Set(resolvedValues)].filter(Boolean);
  }

  const isInvalid = (touched || (multiple && Array.isArray(value) && resolvedValues.length > 3)) && !!error;

  let displayValue;
  let hasValidSelection = false;
  if (multiple) {
    const currentValues = Array.isArray(value) ? value : [];
    hasValidSelection = currentValues.length > 0;
    if (hasValidSelection) {
      const selectedNames = [];
      resolvedValues.forEach((val) => {
        let opt = options.find((o) => String(o.id || o.value) === val);
        if (!opt && userId && val === String(userId)) {
          opt = options.find((o) => String(o.id || o.value) === 'self');
        }
        if (opt) {
          const name = opt.ref_name || opt.chapter_name || opt.name || opt.label;
          if (name && !selectedNames.includes(name)) {
            selectedNames.push(name);
          }
        }
      });

      const actualCount = selectedNames.length || resolvedValues.length;

      if (selectedNames.length > 0) {
        displayValue = actualCount > 1 ? `${actualCount} Selected` : selectedNames.join(', ');
      } else {
        displayValue = actualCount > 0 ? `${actualCount} Selected` : placeholder;
      }
    } else {
      displayValue = placeholder;
    }
  } else {
    const selectedOption = options.find((opt) => String(opt.id || opt.value) === String(value));
    hasValidSelection = !!selectedOption;
    displayValue = selectedOption
      ? selectedOption.ref_name || selectedOption.chapter_name || selectedOption.name || selectedOption.label
      : value
      ? '1 Selected'
      : placeholder;
  }

  return (
    <Form.Group className={`${className} text-start`}>
      {label && (
        <Form.Label
          style={{
            fontSize: '16px',
            fontWeight: '400',
            color: '#333333',
            marginBottom: '6px',
            display: 'block'
          }}
        >
          {label}
          {required && (
            <span className="ms-1" style={{ color: '#005457' }}>
              *
            </span>
          )}
        </Form.Label>
      )}

      <Dropdown
        show={isOpen}
        className="common-select-wrapper"
        autoClose={multiple ? 'outside' : true}
        onToggle={(nextOpen) => {
          setIsOpen(nextOpen);
          if (!nextOpen) {
            setSearchTerm('');
            if (onBlur) {
              setTimeout(() => {
                onBlur({ target: { name, id: `dropdown-${name}` }, persist: () => {} });
              }, 0);
            }
          }
        }}
      >
        <Dropdown.Toggle
          id={`dropdown-${name}`}
          className={`common-select-toggle ${isInvalid ? 'is-invalid' : ''}`}
          disabled={loading}
          onBlur={onBlur}
          style={{
            border: `2px solid ${isInvalid ? '#991B1B' : isValid ? '#005457' : '#000'}`,
            borderRadius: '8px',
            backgroundColor: '#fff',
            height: '46px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 16px',
            boxShadow: 'none'
          }}
        >
          <span
            style={{
              color: hasValidSelection ? '#212121' : '#999',
              fontFamily: "'Inter', sans-serif",
              fontWeight: '400',
              letterSpacing: '0.03em'
            }}
          >
            {loading ? 'Loading...' : displayValue}
          </span>
          <div className="custom-arrow">
            <FaChevronDown size={12} />
          </div>
        </Dropdown.Toggle>

        <Dropdown.Menu
          className="common-select-menu"
          onScroll={(e) => {
            if (!onLoadMore || !hasMore || isLoadingMore) return;
            const bottom = e.target.scrollHeight - e.target.scrollTop - e.target.clientHeight < 50;
            if (bottom) {
              onLoadMore();
            }
          }}
        >
          {(showSearch || onSearch) && (
            <div className="px-3 py-2 sticky-top bg-white border-bottom position-relative">
              <Form.Control
                autoFocus
                size="sm"
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                style={{ paddingRight: '30px', color: '#212121' }}
              />
              {searchTerm && (
                <FaTimes
                  className="position-absolute"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSearchTerm('');
                    if (onSearch) {
                      onSearch('');
                    }
                  }}
                  style={{
                    right: '30px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    cursor: 'pointer',
                    color: '#999',
                    fontSize: '12px'
                  }}
                />
              )}
            </div>
          )}
          {multiple && selectAll && options.length > 0 && (
            <Dropdown.Item
              className="border-bottom"
              onClick={(e) => {
                e.stopPropagation();
                const allSelected = options.every(
                  (opt) => Array.isArray(value) && value.some((v) => String(v) === String(opt.id || opt.value))
                );
                const newValues = allSelected ? [] : options.map((opt) => opt.id || opt.value);
                onChange({ target: { name, value: newValues }, persist: () => {} });
              }}
              style={{ color: '#212121' }}
            >
              <Form.Check
                type="checkbox"
                label="Select All"
                checked={
                  options.length > 0 &&
                  options.every((opt) => Array.isArray(value) && value.some((v) => String(v) === String(opt.id || opt.value)))
                }
                readOnly
                tabIndex={-1}
                style={{ pointerEvents: 'none' }}
              />
            </Dropdown.Item>
          )}
          {options.length === 0 ? (
            <div className="text-center py-3 text-muted small">No options found</div>
          ) : (
            <>
              {options
                .filter((opt) => {
                  // If server-side search is being used, don't filter locally
                  if (onSearch || !searchTerm) return true;
                  const label = opt.ref_name || opt.chapter_name || opt.name || opt.label || '';
                  const desc = opt.desc || opt.description || '';
                  return label.toLowerCase().includes(searchTerm.toLowerCase()) || desc.toLowerCase().includes(searchTerm.toLowerCase());
                })
                .map((opt) => {
                  const optId = opt.id || opt.value;
                  const optLabel = opt.ref_name || opt.chapter_name || opt.name || opt.label || `Item ${optId}`;
                  const isSelected = multiple
                    ? Array.isArray(value) && value.some((v) => String(v) === String(optId))
                    : String(optId) === String(value);
                  const optDesc = opt.desc || opt.description;

                  return (
                    <Dropdown.Item
                      key={optId}
                      active={!multiple && isSelected}
                      className={isSelected ? 'selected-item' : ''}
                      onClick={(e) => {
                        if (multiple) {
                          e.stopPropagation();
                          const currentValues = Array.isArray(value) ? value : [];
                          const newValues = isSelected
                            ? currentValues.filter(
                                (v) => String(v) !== String(optId) && String(v) !== String(opt.ref_name) && String(v) !== String(opt.name)
                              )
                            : [...currentValues, optId];
                          onChange({ target: { name, value: newValues }, persist: () => {} });
                        } else {
                          onChange({ target: { name, value: optId }, persist: () => {} });
                          setIsOpen(false);
                        }
                      }}
                      style={{ display: 'flex', alignItems: 'center', color: '#212121' }}
                    >
                      {multiple && (
                        <Form.Check
                          type="checkbox"
                          checked={isSelected}
                          readOnly
                          tabIndex={-1}
                          style={{ pointerEvents: 'none', marginRight: '10px' }}
                        />
                      )}
                      {optDesc ? (
                        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flexGrow: 1 }}>
                          <span style={{ fontWeight: '500' }}>{optLabel}</span>
                          <span
                            style={{
                              fontSize: '11px',
                              color: '#666',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              marginTop: '2px',
                              display: 'block'
                            }}
                            title={optDesc}
                          >
                            {optDesc}
                          </span>
                        </div>
                      ) : (
                        optLabel
                      )}
                    </Dropdown.Item>
                  );
                })}
            </>
          )}
          {isLoadingMore && (
            <div className="text-center py-2">
              <span className="spinner-border spinner-border-sm text-secondary" role="status" aria-hidden="true"></span>
            </div>
          )}
        </Dropdown.Menu>
      </Dropdown>

      {isInvalid && (
        <Form.Text
          className="mt-1 d-block"
          style={{ fontSize: '14px', fontWeight: '400', color: '#991B1B', marginBottom: '5px' }}
          aria-live="polite"
        >
          {error}
        </Form.Text>
      )}

      {!isInvalid && description && (
        <Form.Text
          className={`mt-1 d-block text-start ${isValid ? 'text-success' : 'text-muted'}`}
          style={{
            fontSize: '14px',
            fontWeight: '400',
            color: isValid ? '#005457' : '#888',
            marginBottom: '5px'
          }}
        >
          {description}
        </Form.Text>
      )}
    </Form.Group>
  );
};

CommonSelect.propTypes = {
  label: PropTypes.string,
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.array.isRequired,
  placeholder: PropTypes.string,
  error: PropTypes.string,
  touched: PropTypes.bool,
  loading: PropTypes.bool,
  name: PropTypes.string,
  className: PropTypes.string,
  required: PropTypes.bool,
  isValid: PropTypes.bool,
  multiple: PropTypes.bool,
  description: PropTypes.string,
  onSearch: PropTypes.func,
  selectAll: PropTypes.bool,
  showSearch: PropTypes.bool,
  onLoadMore: PropTypes.func,
  isLoadingMore: PropTypes.bool,
  hasMore: PropTypes.bool,
  onBlur: PropTypes.func
};

CommonSelect.defaultProps = {
  placeholder: 'Select option...',
  loading: false,
  required: false,
  multiple: false
};

export default CommonSelect;
